import { EditorControl } from '../../common/components/lexical';
import { Flex, VStack, Box, IconButton, Banner } from '@cad-arc';
import { observer } from 'mobx-react';
import React from 'react';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { NewConversationForm } from '../components/new-conversation/new-conversation-form';
import { useNewConversationCancellationModal } from '../hooks/use-new-conversation-cancellation-modal';
import { RecipientsQuickSearchProvider } from '../hooks/use-recipients-quick-search/recipients-quick-search-provider';
import { useRecipientsQuickSearch } from '../hooks/use-recipients-quick-search/use-recipients-quick-search';
import { ViewAllConversationsButton } from './view-all-conversations-button';

type NewConversationParams = {
    isBookmarksView: boolean;
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    setShowAllConversations?: (value: boolean) => void;
};

const _NewConversation: React.FC<NewConversationParams> = observer(
    ({ isBookmarksView, editorControlRef, setShowAllConversations }) => {
        const { setIsOpen } = useNewConversationCancellationModal();

        const {
            isErrorFetchingRecipientLists,
            isFetchingRecipientLists,
            refetchRecipientLists,
        } = useRecipientsQuickSearch();

        const { isFirstResponder } = useApplicationType();

        return (
            <>
                {isErrorFetchingRecipientLists && (
                    <Banner
                        actions={[
                            {
                                children: 'Retry',
                                onClick: () => {
                                    refetchRecipientLists();
                                },
                                isLoading: isFetchingRecipientLists,
                                'data-testid': 'retry-load-recipients-list',
                            },
                        ]}
                        description="Try again later or contact IT for support."
                        status="attention"
                        title="Failed to load the recipients list."
                    />
                )}
                {isFirstResponder && (
                    <ViewAllConversationsButton
                        isBookmarksView={isBookmarksView}
                        setShowAllConversations={setShowAllConversations}
                    />
                )}
                <Flex
                    data-testid="new-conversation-subpanel"
                    flexWrap="wrap"
                    w="100%"
                    h="100%"
                    overflowY="auto"
                >
                    <VStack position="relative" alignItems="start" w="100%">
                        <Box
                            position="absolute"
                            right="var(--arc-space-2)"
                            top="var(--arc-space-1)"
                            zIndex={1}
                            data-testid="close-new-conversation-form"
                        >
                            <IconButton
                                variant="ghost"
                                icon="CloseX"
                                aria-label="close"
                                onClick={() => {
                                    setIsOpen?.(true);
                                }}
                            />
                        </Box>
                        <NewConversationForm editorControlRef={editorControlRef} />
                    </VStack>
                </Flex>
            </>
        );
    }
);

export const NewConversation: React.FC<NewConversationParams> = ({ children, ...props }) => {
    return (
        <RecipientsQuickSearchProvider>
            <_NewConversation {...props}>{children}</_NewConversation>
        </RecipientsQuickSearchProvider>
    );
};
